import React, { useState, useEffect, useRef } from 'react';
import { motion, AnimatePresence, useScroll, useTransform, useMotionValueEvent } from 'framer-motion';
import Hero from './components/Hero';
import Story from './components/Story';
import Gallery from './components/Gallery';
import Details from './components/Details';
import RSVP from './components/RSVP';
import Footer from './components/Footer';
import Loading from './components/Loading';
import InvitationError from './components/InvitationError';
import { getInvitation, getCodeFromURL } from './utils/api';
import InvitationGate from './components/InvitationGate';
import { gallery12, gallery14, gallery15, heroImg } from './assets';
import Wishes from './components/Wishes';
import Introduction from './components/Introduction';
import FloatingButtons from './components/FloatingButtons';
import { preloadAssets, waitForFonts } from './utils/preloader';

function App() {
  const [loading, setLoading] = useState(true);
  const [loadingProgress, setLoadingProgress] = useState(0);
  const [invitation, setInvitation] = useState(null);
  const [error, setError] = useState(null);
  const [hasRSVP, setHasRSVP] = useState(false);
  const [hasWish, setHasWish] = useState(false);
  const [opened, setOpened] = useState(false);
  const audioRef = useRef(null);
  const [wishesRefreshTrigger, setWishesRefreshTrigger] = useState(0);

  // Inside your component:
  const imageRef = useRef(null);

  // Track scroll progress of this specific element
  const { scrollYProgress } = useScroll({
    target: imageRef,
    offset: ["start end", "end start"],
  });

  const opacity = useTransform(scrollYProgress, [0, 0.5, 1], [0.3, 1, 0]);

  useEffect(() => {
    if (!loading && invitation) {
      const timer = setTimeout(() => {
        window.dispatchEvent(new Event('resize')); // ← This fixes it
        window.scrollTo(0, 0);
      }, 100);
      return () => clearTimeout(timer);
    }
  }, [loading, invitation]);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  useEffect(() => {
    if (opened && audioRef.current) {
      audioRef.current.volume = 0.5; // don’t blast ears
      audioRef.current.play().catch(() => {
        console.warn("Autoplay blocked, waiting for interaction");
      });
    }
  }, [opened]);

  useEffect(() => {
    const loadInvitation = async () => {
      const code = getCodeFromURL();

      if (!code) {
        setError('No invitation code provided. Please use the link sent to you.');
        setLoading(false);
        return;
      }

      try {
        // Step 1: Load invitation data (20% progress)
        setLoadingProgress(10);
        const data = await getInvitation(code);
        if (data.success) {
          setInvitation(data.invitation);
          console.log(data.invitation)
          setHasRSVP(data.has_rsvp);
          setHasWish(data.has_wish);
          setLoadingProgress(20);

          // Step 2: Preload images (20% -> 80%)
          const imagesToPreload = [
            gallery12,
            gallery15,
            heroImg,
          ];

          await preloadAssets(imagesToPreload, (progress) => {
            // Map progress from 20% to 80%
            const mappedProgress = 20 + (progress * 0.6);
            setLoadingProgress(mappedProgress);
          });

          // Step 3: Wait for fonts (80% -> 90%)
          setLoadingProgress(85);
          await waitForFonts();
          setLoadingProgress(90);

          // Step 4: Small delay to ensure everything is ready (90% -> 100%)
          await new Promise(resolve => setTimeout(resolve, 300));
          setLoadingProgress(100);

          // Small delay before hiding loading screen
          await new Promise(resolve => setTimeout(resolve, 200));

        } else {
          setError(data.message || 'Invalid invitation code');
        }
      } catch (err) {
        setError(err.message || 'Failed to load invitation. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    loadInvitation();
  }, []);

  // Show loading state
  if (loading) {
    return <Loading progress={loadingProgress} />;
  }

  // Show error if invitation not found
  if (error || !invitation) {
    return <InvitationError message={error} />;
  }

  const refreshWishes = () => {
    setWishesRefreshTrigger(prev => prev + 1);
  };

  return (
    <div className="relative overflow-x-hidden">
      {/* Grain texture overlay */}
      <div
        className="fixed inset-0 pointer-events-none z-50 mix-blend-overlay opacity-[0.03]"
        style={{
          backgroundImage: `url("data:image/svg+xml,%3Csvg viewBox='0 0 400 400' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noiseFilter'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='2.5' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noiseFilter)'/%3E%3C/svg%3E")`
        }}
      />
      {/* Background music */}
      <audio ref={audioRef} loop>
        <source src="/audio/bg-music.mp3" type="audio/mpeg" />
      </audio>

      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ duration: 1 }}
        >
          <InvitationGate
            guest_name={invitation.guest_name}
            opened={opened}
            setOpened={setOpened}
          />
          <Hero invitation={invitation} />
          <Introduction />
          <Story />
          <motion.div
            initial={{ opacity: 0.2 }}
            whileInView={{ opacity: 1 }}
            viewport={{
              once: false,
              amount: 0.3,
              margin: "0px 0px -200px 0px" // Start fading earlier
            }}
            transition={{ duration: 0.8 }}
            className="w-full"
          >
            <div className="w-full">
              <img src={gallery15} alt="" className='w-full max-h-[800px] md:hidden object-cover' />
              <img src={gallery12} alt="" className='w-full max-h-[800px] hidden md:block object-cover' />
            </div>
          </motion.div>
          <Details eventType={invitation.event_type} />
          <Gallery audioRef={audioRef} />
          {invitation.guest_name !== 'Padus GII Holis' && (
            <RSVP
              invitation={invitation}
              hasRSVP={hasRSVP}
              onRSVPSubmit={refreshWishes}
              eventType={invitation.event_type}
            />
          )}
          <Wishes invitation={invitation} refreshTrigger={wishesRefreshTrigger} />
          <Footer />
        </motion.div>
      </AnimatePresence>

      <FloatingButtons audioRef={audioRef} opened={opened} />
    </div>
  );
}

export default App;
